import React, { useState, useEffect } from 'react';
import { DOCUMENT_TYPE_OPTIONS } from '../../utils/documentTypes';
import {
  createDocumentNumberSeries,
  updateDocumentNumberSeries,
} from '../../services/documentNumberSeriesService';

const DocumentNumberSeriesForm = ({ isOpen, onClose, item = null, onSuccess }) => {
  const [formData, setFormData] = useState({
    series: '',
    document_type: '',
    next_number: 1,
    padding_length: 1,
    description: '',
    is_active: true,
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (item) {
      setFormData({
        series: item.series || '',
        document_type: item.document_type || '',
        next_number: item.next_number ?? 1,
        padding_length: item.padding_length ?? 1,
        description: item.description || '',
        is_active: item.is_active !== undefined ? item.is_active : true,
      });
    } else {
      setFormData({
        series: '',
        document_type: '',
        next_number: 1,
        padding_length: 1,
        description: '',
        is_active: true,
      });
    }
    setErrors({});
  }, [item, isOpen]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    let processed = type === 'checkbox' ? checked : value;

    if (name === 'series') {
      processed = value.toUpperCase().replace(/\s+/g, '').slice(0, 20);
    }
    if (name === 'next_number' || name === 'padding_length') {
      processed = value === '' ? '' : parseInt(value, 10);
    }

    setFormData((prev) => ({ ...prev, [name]: processed }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const validate = () => {
    const nextErrors = {};
    if (!formData.series.trim()) {
      nextErrors.series = 'La serie es obligatoria';
    }
    if (formData.next_number === '' || Number(formData.next_number) < 1) {
      nextErrors.next_number = 'El siguiente número debe ser al menos 1';
    }
    if (formData.padding_length === '' || Number(formData.padding_length) < 1) {
      nextErrors.padding_length = 'Los dígitos deben ser al menos 1';
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      const payload = {
        series: formData.series.trim(),
        document_type: formData.document_type || null,
        next_number: parseInt(formData.next_number, 10),
        padding_length: parseInt(formData.padding_length, 10),
        description: formData.description || null,
        is_active: formData.is_active,
      };

      const response = item
        ? await updateDocumentNumberSeries(item.id, payload)
        : await createDocumentNumberSeries(payload);

      if (response.success) {
        onSuccess(response.data);
        onClose();
      } else {
        setErrors({ submit: response.message || 'Error al guardar' });
      }
    } catch (error) {
      const apiErrors = error.response?.data?.errors;
      if (apiErrors) {
        const mapped = {};
        Object.entries(apiErrors).forEach(([key, value]) => {
          mapped[key] = Array.isArray(value) ? value[0] : value;
        });
        setErrors(mapped);
      } else {
        setErrors({ submit: error.response?.data?.message || 'Error al guardar la serie' });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {item ? 'Editar serie autonumerada' : 'Nueva serie autonumerada'}
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {errors.submit && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {errors.submit}
            </div>
          )}

          <div>
            <label htmlFor="series" className="block text-sm font-medium text-gray-700 mb-1">
              Serie <span className="text-red-500">*</span>
            </label>
            <input
              id="series"
              name="series"
              value={formData.series}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
              placeholder="Ej: IE2, F001"
            />
            {errors.series && <p className="mt-1 text-sm text-red-600">{errors.series}</p>}
          </div>

          <div>
            <label htmlFor="document_type" className="block text-sm font-medium text-gray-700 mb-1">
              Tipo de documento
            </label>
            <select
              id="document_type"
              name="document_type"
              value={formData.document_type}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
            >
              <option value="">Todos los tipos</option>
              {DOCUMENT_TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-500">
              Si deja vacío, la serie aplica a cualquier tipo de documento.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="next_number" className="block text-sm font-medium text-gray-700 mb-1">
                Siguiente número <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                id="next_number"
                name="next_number"
                min="1"
                value={formData.next_number}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
              {errors.next_number && <p className="mt-1 text-sm text-red-600">{errors.next_number}</p>}
            </div>
            <div>
              <label htmlFor="padding_length" className="block text-sm font-medium text-gray-700 mb-1">
                Dígitos del número
              </label>
              <input
                type="number"
                id="padding_length"
                name="padding_length"
                min="1"
                max="20"
                value={formData.padding_length}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
              <p className="mt-1 text-xs text-gray-500">Use 1 para 4, 5, 6… Use 8 para 00000001.</p>
              {errors.padding_length && <p className="mt-1 text-sm text-red-600">{errors.padding_length}</p>}
            </div>
          </div>

          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
              Descripción
            </label>
            <textarea
              id="description"
              name="description"
              rows={2}
              value={formData.description}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
              placeholder="Ej: Facturas internas IE2"
            />
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              name="is_active"
              checked={formData.is_active}
              onChange={handleChange}
              className="rounded border-gray-300 text-teal-600 focus:ring-teal-500"
            />
            <span className="text-sm text-gray-800">Serie activa</span>
          </label>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 rounded-md disabled:opacity-50"
            >
              {loading ? 'Guardando...' : item ? 'Actualizar' : 'Crear'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DocumentNumberSeriesForm;
